import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
// Import existing admin components that marketing manager can access
import ArticlesTab from "../components/admin/ArticlesTab";
import MarketingTab from "../components/admin/MarketingTab";
import AmbassadorManagementTab from "../components/admin/AmbassadorManagementTab";
import SalesAgentsTab from "../components/admin/SalesAgentsTab";
import AnalyticsDashboard from "../components/admin/AnalyticsDashboard";
import SocialMediaTab from "../components/admin/SocialMediaTab";
import { toast } from "sonner";
import { 
  Newspaper, Megaphone, Award, Users, TrendingUp, 
  Eye, Calendar, BarChart3, Target, Share2, Briefcase,
  FileText, Image, Mail, MessageSquare
} from "lucide-react";
import { API } from "../apiConfig";


const MarketingDashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role !== "marketing_manager" && user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, articlesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/articles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/sales-agents`, { headers }).catch(() => ({ data: [] })),
      ]);

      setStats(statsRes.data);
      setArticles(articlesRes.data || []);
      setSalesAgents(agentsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading Marketing Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="marketing-dashboard">
      <Navigation />

      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Marketing Dashboard</h1>
                <p className="text-muted-foreground">Welcome back, {user?.name}</p>
              </div>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Newspaper className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{articles.length}</p>
                    <p className="text-sm text-muted-foreground">Articles</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Award className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{salesAgents.length}</p>
                    <p className="text-sm text-muted-foreground">Sales Agents</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-muted/50 p-1 rounded-2xl flex-wrap h-auto gap-1">
              <TabsTrigger value="overview" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <BarChart3 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <TrendingUp className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="articles" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <Newspaper className="h-4 w-4" />
                Articles & Blog
              </TabsTrigger>
              <TabsTrigger value="marketing" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <Megaphone className="h-4 w-4" />
                Campaigns
              </TabsTrigger>
              <TabsTrigger value="ambassadors" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <Award className="h-4 w-4" />
                Ambassadors
              </TabsTrigger>
              <TabsTrigger value="agents" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <Briefcase className="h-4 w-4" />
                Sales Agents
              </TabsTrigger>
              <TabsTrigger value="social" className="rounded-xl gap-2 data-[state=active]:bg-background">
                <Share2 className="h-4 w-4" />
                Social Media
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>Common marketing tasks</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      className="w-full justify-start rounded-xl" 
                      variant="outline"
                      onClick={() => setActiveTab("articles")}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Write New Article
                    </Button>
                    <Button 
                      className="w-full justify-start rounded-xl" 
                      variant="outline"
                      onClick={() => setActiveTab("marketing")}
                    >
                      <Megaphone className="h-4 w-4 mr-2" />
                      Create Campaign
                    </Button>
                    <Button 
                      className="w-full justify-start rounded-xl" 
                      variant="outline"
                      onClick={() => setActiveTab("social")}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule Social Post
                    </Button>
                    <Button 
                      className="w-full justify-start rounded-xl" 
                      variant="outline"
                      onClick={() => setActiveTab("ambassadors")}
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Review Ambassador Applications
                    </Button>
                  </CardContent>
                </Card>

                {/* Recent Articles */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle>Recent Articles</CardTitle>
                    <CardDescription>Latest blog posts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {articles.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No articles yet. Create your first one!</p>
                    ) : (
                      <div className="space-y-3">
                        {articles.slice(0, 5).map((article) => (
                          <div key={article.article_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                            <div>
                              <p className="font-medium text-sm">{article.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(article.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge variant={article.published ? "default" : "secondary"}>
                              {article.published ? "Published" : "Draft"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <AnalyticsDashboard token={token} />
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <ArticlesTab token={token} />
            </TabsContent>

            {/* Marketing Campaigns Tab */}
            <TabsContent value="marketing">
              <MarketingTab token={token} />
            </TabsContent>

            {/* Ambassadors Tab */}
            <TabsContent value="ambassadors">
              <AmbassadorManagementTab token={token} />
            </TabsContent>

            {/* Sales Agents Tab */}
            <TabsContent value="agents">
              <SalesAgentsTab agents={salesAgents} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Social Media Tab */}
            <TabsContent value="social">
              <SocialMediaTab token={token} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MarketingDashboardPage;
